import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Pause, Play, MoreHorizontal, Trash2, Eye, Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import axiosInstance from '../../axiosInstance';
import { Avatar } from '../common';
import { toast } from '../toast';
import UserDisplay from '../UserDisplay';

const StoryViewer = ({
  isOpen,
  onClose,
  stories, // Array of user story groups: [{ user, stories: [...], is_own }, ...]
  initialUserIndex = 0,
  initialStoryIndex = 0,
  onStoryViewed,
  onStoryDeleted,
}) => {
  const { t } = useTranslation('feed');
  const navigate = useNavigate();
  const [currentUserIndex, setCurrentUserIndex] = useState(initialUserIndex);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(initialStoryIndex);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [loadingViewers, setLoadingViewers] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const progressInterval = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const currentUser = useSelector(state => state.auth?.user);

  const currentUserStories = stories[currentUserIndex];
  const currentStory = currentUserStories?.stories?.[currentStoryIndex];
  const isOwnStory = currentUserStories?.is_own || currentUserStories?.user?.id === currentUser?.id;

  // Reset state when story changes
  useEffect(() => {
    setProgress(0);
    setShowViewers(false);
    setShowMenu(false);
  }, [currentUserIndex, currentStoryIndex]);

  // Mark story as viewed
  useEffect(() => {
    if (currentStory && !currentStory.is_viewed && !isOwnStory) {
      axiosInstance.post(`/stories/${currentStory.id}/view`)
        .then(() => {
          onStoryViewed?.(currentStory.id);
        })
        .catch(console.error);
    }
  }, [currentStory, isOwnStory, onStoryViewed]);

  // Progress timer
  useEffect(() => {
    if (!isOpen || !currentStory || isPaused) {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      return;
    }

    const duration = (currentStory.duration || 5) * 1000; // Convert to milliseconds
    const interval = 50; // Update every 50ms for smooth progress
    const step = (interval / duration) * 100;

    progressInterval.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          goToNext();
          return 0;
        }
        return prev + step;
      });
    }, interval);

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [isOpen, currentStory, isPaused, currentUserIndex, currentStoryIndex]);

  const goToPrevious = useCallback(() => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
      setProgress(0);
    } else if (currentUserIndex > 0) {
      const prevUserIndex = currentUserIndex - 1;
      setCurrentUserIndex(prevUserIndex);
      setCurrentStoryIndex(stories[prevUserIndex].stories.length - 1);
      setProgress(0);
    }
  }, [currentStoryIndex, currentUserIndex, stories]);

  const goToNext = useCallback(() => {
    const userStories = stories[currentUserIndex]?.stories || [];

    if (currentStoryIndex < userStories.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
      setProgress(0);
    } else if (currentUserIndex < stories.length - 1) {
      setCurrentUserIndex(currentUserIndex + 1);
      setCurrentStoryIndex(0);
      setProgress(0);
    } else {
      onClose();
    }
  }, [currentStoryIndex, currentUserIndex, stories, onClose]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsPaused(true);
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = touchEndX - touchStartX.current;
    const diffY = touchEndY - touchStartY.current;

    setIsPaused(false);

    // Only handle horizontal swipes (ignore vertical for scrolling)
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (diffX > 0) {
        goToPrevious();
      } else {
        goToNext();
      }
    }
  };

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const third = rect.width / 3;

    if (x < third) {
      goToPrevious();
    } else if (x > third * 2) {
      goToNext();
    }
  };

  const handleMouseDown = () => setIsPaused(true);
  const handleMouseUp = () => setIsPaused(false);

  const fetchViewers = async () => {
    if (!currentStory || !isOwnStory) return;

    setLoadingViewers(true);
    try {
      const response = await axiosInstance.get(`/stories/${currentStory.id}/viewers`);
      if (response.data.success) {
        setViewers(response.data.viewers || []);
      }
    } catch (error) {
      console.error('Failed to fetch viewers:', error);
    } finally {
      setLoadingViewers(false);
    }
  };

  const handleShowViewers = () => {
    setShowViewers(true);
    setIsPaused(true);
    fetchViewers();
  };

  const handleHideViewers = () => {
    setShowViewers(false);
    setIsPaused(false);
  };

  const handleDeleteStory = async () => {
    if (!currentStory) return;

    try {
      const response = await axiosInstance.delete(`/stories/${currentStory.id}`);
      if (response.data.success) {
        toast.success(t('story.deleted'));
        onStoryDeleted?.(currentStory.id);
        goToNext();
      }
    } catch (error) {
      console.error('Failed to delete story:', error);
      toast.error(t('story.deleteFailed'));
    }
    setShowMenu(false);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return t('story.justNow');
    if (diffMins < 60) return t('story.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('story.hoursAgo', { count: diffHours });
    return date.toLocaleDateString();
  };

  if (!isOpen || !currentStory) return null;

  return (
    <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center">
      {/* Story container */}
      <div
        className="relative w-full max-w-md h-full max-h-[100dvh] bg-black"
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        {/* Progress bars */}
        <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2 pt-[env(safe-area-inset-top)]">
          {currentUserStories?.stories?.map((story, index) => (
            <div
              key={story.id}
              className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden"
            >
              <div
                className="h-full bg-white rounded-full transition-all duration-100"
                style={{
                  width:
                    index < currentStoryIndex
                      ? '100%'
                      : index === currentStoryIndex
                      ? `${progress}%`
                      : '0%',
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 pt-[calc(env(safe-area-inset-top)+1.5rem)] bg-gradient-to-b from-black/50 to-transparent">
          <button
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              const storyUsername = currentUserStories?.user?.username;
              if (storyUsername) {
                onClose();
                navigate(`/${storyUsername}`);
              }
            }}
          >
            <Avatar
              user={currentUserStories?.user}
              size="sm"
              className="ring-2 ring-white"
            />
            <div className="text-left">
              <UserDisplay user={currentUserStories?.user} size="sm" nameClassName="text-white" />
              <p className="text-white/70 text-xs">
                {formatTimeAgo(currentStory.created_at)}
              </p>
            </div>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsPaused(!isPaused);
              }}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              {isPaused ? (
                <Play className="w-5 h-5 text-white" />
              ) : (
                <Pause className="w-5 h-5 text-white" />
              )}
            </button>

            {isOwnStory && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                  setIsPaused(true);
                }}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <MoreHorizontal className="w-5 h-5 text-white" />
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Story content */}
        <div className="w-full h-full flex items-center justify-center">
          {currentStory.media_type === 'video' ? (
            <video
              key={currentStory.id}
              src={currentStory.media_url}
              className="w-full h-full object-contain"
              autoPlay
              playsInline
              muted={false}
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              onPause={() => setIsPaused(true)}
              onPlay={() => setIsPaused(false)}
            />
          ) : (
            <img
              key={currentStory.id}
              src={currentStory.media_url}
              alt={t('story.storyAlt')}
              className="w-full h-full object-contain"
            />
          )}
        </div>

        {/* Caption */}
        {currentStory.caption && (
          <div className="absolute bottom-0 left-0 right-0 z-20 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-sm">{currentStory.caption}</p>
          </div>
        )}

        {/* Views count for own stories */}
        {isOwnStory && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleShowViewers();
            }}
            className="absolute bottom-4 left-4 z-20 flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full"
          >
            <Eye className="w-4 h-4 text-white" />
            <span className="text-white text-sm">{currentStory.views_count || 0}</span>
          </button>
        )}

        {/* Navigation buttons (desktop) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToPrevious();
          }}
          className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <ChevronLeft className="w-6 h-6 text-white rtl:-scale-x-100" />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            goToNext();
          }}
          className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <ChevronRight className="w-6 h-6 text-white rtl:-scale-x-100" />
        </button>

        {/* Menu dropdown */}
        {showMenu && (
          <div
            className="absolute top-20 right-4 z-30 bg-gray-900 rounded-xl overflow-hidden shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleDeleteStory}
              className="flex items-center gap-3 w-full px-4 py-3 text-red-500 hover:bg-white/10 transition-colors"
            >
              <Trash2 className="w-5 h-5" />
              <span>{t('story.deleteStory')}</span>
            </button>
          </div>
        )}

        {/* Viewers panel */}
        {showViewers && (
          <div
            className="absolute inset-0 z-30 bg-black/90 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-white font-semibold">
                {t('story.viewers', { count: viewers.length })}
              </h3>
              <button
                onClick={handleHideViewers}
                className="p-2 rounded-full hover:bg-white/10"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {loadingViewers ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 text-[var(--color-primary-500)] animate-spin" />
                </div>
              ) : viewers.length === 0 ? (
                <p className="text-center text-white/60 py-8">{t('story.noViewers')}</p>
              ) : (
                <div className="space-y-3">
                  {viewers.map((view) => (
                    <div key={view.user?.id} className="flex items-center gap-3">
                      <Avatar user={view.user} size="sm" />
                      <div>
                        <UserDisplay user={view.user} size="sm" nameClassName="text-white" />
                        <p className="text-white/50 text-xs">
                          {formatTimeAgo(view.viewed_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryViewer;
